import dayjs from 'dayjs';

export const FILTER_LIMITS = { leaves: 32, nodes: 64, levels: 4, text: 256 };
export const FILTER_FIELDS = [
  { value: 'file_name', label: '文件名' },
  { value: 'directory', label: '目录' },
  { value: 'extension', label: '扩展名' },
  { value: 'file_size', label: '文件大小' },
  { value: 'modified_at', label: '修改时间' },
];
export const SIZE_UNITS = { B: 1, KB: 1024, MB: 1048576, GB: 1073741824 };
const TEXT_OPERATORS = [
  { value: 'contains', label: '包含' },
  { value: 'not_contains', label: '不包含' },
  { value: 'equals', label: '等于' },
  { value: 'not_equals', label: '不等于' },
  { value: 'regex', label: '正则匹配' },
];
const TYPED_OPERATORS = [
  { value: 'equals', label: '等于' },
  { value: 'not_equals', label: '不等于' },
  { value: 'gt', label: '大于' },
  { value: 'gte', label: '大于等于' },
  { value: 'lt', label: '小于' },
  { value: 'lte', label: '小于等于' },
  { value: 'between', label: '介于（含边界）' },
];
const fieldType = field => field === 'file_size' ? 'size' : field === 'modified_at' ? 'date' : 'text';
export const getFilterOperators = field => fieldType(field) === 'text' ? TEXT_OPERATORS : TYPED_OPERATORS;
let nextId = 0;
const localId = () => `waveform-filter-${++nextId}`;
const emptyValue = (field, operator) => operator === 'between' ? [null, null] : fieldType(field) === 'text' ? '' : null;

export const createFilterRule = () => ({
  id: localId(), field: 'file_name', operator: 'contains', value: '', unit: 'B',
});
export const createFilterGroup = () => ({ id: localId(), combinator: 'and', rules: [createFilterRule()] });

export function changeFilterField(rule, field) {
  const operator = getFilterOperators(field).some(item => item.value === rule.operator)
    ? rule.operator : getFilterOperators(field)[0].value;
  return {
    ...rule, field, operator,
    value: fieldType(field) === fieldType(rule.field) ? rule.value : emptyValue(field, operator),
    unit: field === 'file_size' && rule.field === 'file_size' ? rule.unit : 'B',
  };
}

export function changeFilterOperator(rule, operator) {
  return {
    ...rule, operator,
    value: (operator === 'between') !== (rule.operator === 'between') ? emptyValue(rule.field, operator) : rule.value,
  };
}

export function getFilterStats(tree) {
  let nodes = 0;
  let leaves = 0;
  const visit = node => {
    nodes += 1;
    if (Array.isArray(node.rules)) node.rules.forEach(visit);
    else leaves += 1;
  };
  visit(tree);
  return { nodes, leaves };
}

// Build a detached API snapshot; no draft identity or display unit crosses the wire.
export function serializeFilters(tree) {
  let nodes = 0;
  let leaves = 0;
  const fail = message => { throw new Error(message); };
  const typedValue = (rule, value) => {
    if (rule.field === 'file_size') {
      const factor = SIZE_UNITS[rule.unit || 'B'];
      if (!factor) fail('请选择有效的大小单位');
      if (typeof value !== 'number' || !Number.isFinite(value)) fail('请填写有效的文件大小');
      if (value < 0) fail('文件大小不能为负数');
      if (factor === 1 && !Number.isInteger(value)) fail('B 单位请填写整数，小数大小请改用 KB、MB 或 GB');
      const bytes = value * factor;
      if (!Number.isFinite(bytes) || bytes > Number.MAX_SAFE_INTEGER) fail('文件大小过大，请减小数值或单位');
      return Math.round(bytes);
    }
    if (!dayjs.isDayjs(value) || !value.isValid()) fail('请选择有效的修改时间');
    return value.startOf('minute').toISOString();
  };
  const visit = (node, level) => {
    nodes += 1;
    if (nodes > FILTER_LIMITS.nodes) fail('最多 64 个条件节点');
    if (!node || typeof node !== 'object') fail('条件无效');
    if (Object.prototype.hasOwnProperty.call(node, 'rules')) {
      if (level > FILTER_LIMITS.levels) fail('条件组最多 4 层');
      if (!['and', 'or'].includes(node.combinator)) fail('条件组组合方式无效');
      if (!Array.isArray(node.rules) || !node.rules.length) fail('条件组不能为空，请添加条件');
      return { combinator: node.combinator, rules: node.rules.map(child => visit(child, level + 1)) };
    }
    leaves += 1;
    if (leaves > FILTER_LIMITS.leaves) fail('最多 32 个条件');
    if (!FILTER_FIELDS.some(field => field.value === node.field)) fail('条件字段无效');
    if (!getFilterOperators(node.field).some(operator => operator.value === node.operator)) fail('条件运算符无效');
    let value;
    if (fieldType(node.field) === 'text') {
      if (typeof node.value !== 'string' || !node.value.trim()) fail('请填写完整的条件值');
      if (node.value.length > FILTER_LIMITS.text) fail('条件值最多 256 个字符');
      if (node.operator === 'regex') {
        try { new RegExp(node.value, 'i'); } catch { fail('正则表达式无效，请检查条件值'); }
      }
      value = node.value;
    } else if (node.operator === 'between') {
      if (!Array.isArray(node.value) || node.value.length !== 2) fail('请填写完整的范围条件');
      value = node.value.map(bound => typedValue(node, bound));
      if (value[0] > value[1]) fail('范围起点不能大于终点（含边界）');
    } else {
      value = typedValue(node, node.value);
    }
    return { field: node.field, operator: node.operator, value };
  };
  if (!tree || !Array.isArray(tree.rules)) fail('请添加条件组');
  return visit(tree, 1);
}
